<template>
  <q-page>
    <div class="row">
      <div class="col">
        <div class="banner">
          <Banner v-if="profileUpdateSuccess" mode="positive">
            <div>
              Profile updated.
            </div>
          </Banner>
          <Banner v-if="profileUpdateError" mode="negative">
            <div>
              Error updating profile.
            </div>
          </Banner>
          <Banner v-if="preferenceUpdateSuccess" mode="positive">
            <div>
              Preferences updated.
            </div>
          </Banner>
          <Banner v-if="preferenceUpdateError" mode="negative">
            <div>
              Error updating preferences.
            </div>
          </Banner>
        </div>
        <q-card flat no-border>
          <q-card-section>
            <q-tabs
              v-model="tab"
              class="text-grey"
              align="left"
              active-color="primary"
              indicator-color="primary"
              dense
              narrow-indicator
              :breakpoint="0"
            >
              <q-tab name="profile" :ripple="false" label="Profile"></q-tab>
              <q-tab
                name="coursesPlayed"
                :ripple="false"
                label="Courses Played"
              ></q-tab>
              <q-tab name="ratings" :ripple="false" label="Ratings"></q-tab>
              <q-tab
                name="preferences"
                :ripple="false"
                label="Preferences"
              ></q-tab>
            </q-tabs>
            <q-tab-panels v-model="tab">
              <q-tab-panel name="profile">
                <div v-if="userProfile">
                  <div class="text-h5">User Profile</div>
                  <q-form @submit.prevent class="q-mt-lg q-mb-lg">
                    <div class="column justify-evenly">
                      <div class="row">
                        <div class="col-12 col-md-4">
                          <div class="q-px-sm q-pb-sm">
                            <q-input
                              filled
                              v-model.trim="firstName"
                              type="text"
                              name="nameFirst"
                              id="nameFirst"
                              label="First Name"
                              :rules="[
                                val => !!val || 'First Name is required',
                              ]"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-4">
                          <div class="q-px-sm q-pb-sm">
                            <q-input
                              filled
                              v-model.trim="lastName"
                              type="text"
                              name="nameLast"
                              id="nameLast"
                              label="Last Name"
                              :rules="[val => !!val || 'Last Name is required']"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-4">
                          <div class="q-px-sm q-pb-sm">
                            <q-input
                              filled
                              v-model.trim="displayName"
                              type="text"
                              name="displayName"
                              id="displayName"
                              label="Display Name"
                              :rules="[
                                val => !!val || 'Display Name is required',
                              ]"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-12 col-md-3">
                          <div class="q-px-sm q-pb-sm">
                            <q-input
                              filled
                              v-model.trim="streetAddress"
                              type="text"
                              name="address"
                              id="address"
                              label="Address"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-3">
                          <div class="q-px-sm q-pb-sm">
                            <q-input
                              filled
                              v-model.trim="city"
                              type="text"
                              name="city"
                              id="city"
                              label="City"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-3">
                          <div class="q-px-sm q-pb-sm">
                            <q-select
                              clearable
                              filled
                              v-model="state"
                              option-value="abbreviation"
                              option-label="name"
                              :options="usStates"
                              label="State"
                              emit-value
                              map-options
                              :rules="[val => true]"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-3">
                          <div class="q-px-sm q-pb-sm">
                            <q-input
                              filled
                              v-model.trim="zipCode"
                              type="text"
                              name="zipCode"
                              id="zipCode"
                              label="Zip Code"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-12 col-md-4">
                          <div class="q-px-sm q-pb-sm">
                            <q-select
                              clearable
                              filled
                              v-model="country"
                              option-value="code"
                              option-label="name"
                              :options="countries"
                              label="Country"
                              emit-value
                              map-options
                              :rules="[val => true]"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-4">
                          <div class="q-px-sm q-pb-sm">
                            <q-input
                              filled
                              v-model.trim="phoneNumber"
                              type="text"
                              name="phoneNumber"
                              id="phoneNumber"
                              label="Phone Number"
                              mask="(###) ### - ####"
                              hint="(###) ### - ####"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-4">
                          <div class="q-px-sm q-pb-sm">
                            <q-input
                              filled
                              v-model.trim="birthDate"
                              type="text"
                              name="birthDate"
                              id="birthDate"
                              label="Birth Date"
                              mask="##/##/####"
                              hint="MM/DD/YYYY"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-12 col-md-4">
                          <div class="q-px-sm q-pb-sm">
                            <q-input
                              filled
                              v-model.trim="ghin"
                              type="text"
                              name="ghin"
                              id="ghin"
                              label="GHIN"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-4">
                          <div class="q-px-sm q-pb-sm">
                            <q-select
                              clearable
                              filled
                              v-model="handicap"
                              :options="handicapRangeOptions"
                              label="Handicap?"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-4">
                          <div class="q-px-sm q-pb-sm">
                            <q-input
                              filled
                              v-model.trim="homeCourse"
                              type="text"
                              name="homeCourse"
                              id="homeCourse"
                              label="Home Course(s)"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-12 col-md-6">
                          <div class="q-px-sm q-pb-sm">
                            <q-select
                              clearable
                              filled
                              v-model="tripsPerYear"
                              :options="tripsPerYearOptions"
                              label="How many golf trips do you take per year?"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-6">
                          <div class="q-px-sm q-pb-sm">
                            <q-select
                              clearable
                              filled
                              v-model="roundsPerYear"
                              :options="roundsPerYearOptions"
                              label="How many rounds do you play in a year?"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-12 col-md-6">
                          <div class="q-px-sm q-pb-sm">
                            <q-select
                              clearable
                              filled
                              v-model="designSchool"
                              :options="designSchoolOptions"
                              label="Favorite design school?"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-md-6">
                          <div class="q-px-sm q-pb-sm">
                            <q-select
                              clearable
                              filled
                              v-model="coursePreference"
                              :options="coursePreferenceOptions"
                              label="What do you value most in a golf course?"
                              :rules="[val => true]"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-12 col-md-6">
                          <div class="q-px-sm q-pb-sm">
                            <q-select
                              clearable
                              filled
                              v-model="gender"
                              :options="genderOptions"
                              label="Gender"
                              :rules="[val => true]"
                            ></q-select>
                          </div>
                        </div>

                        <div class="col-12 col-md-6">
                          <div class="q-px-sm q-pb-sm">
                            <q-select
                              clearable
                              filled
                              emit-value
                              map-options
                              v-model="isPrivateMember"
                              :options="memberOptions"
                              label="Are you a member of a private club?"
                            ></q-select>
                          </div>
                        </div>
                      </div>
                    </div>

                    <br />
                    <p class="q-ml-sm">
                      <q-checkbox v-model="receiveEmails">
                        Receive occasional emails about Open Links product
                        enhancements, best practices and blogs</q-checkbox
                      >
                    </p>
                    <q-btn
                      :ripple="false"
                      :disabled="!isSaveButtonEnabled"
                      push
                      color="primary"
                      @click="save"
                      >Save</q-btn
                    >
                    <q-btn :ripple="false" flat no-caps @click="goBack"
                      >Cancel</q-btn
                    >
                  </q-form>
                </div>
              </q-tab-panel>
              <q-tab-panel name="coursesPlayed">
                <div>
                  <div class="text-h5">
                    Courses Played
                    <span v-if="coursePlayedCoursesSorted.length"
                      >({{ coursePlayedCoursesSorted.length }})</span
                    >
                  </div>
                  <div class="q-mt-lg q-mb-lg">
                    <ol>
                      <li
                        v-for="course in coursePlayedCoursesSorted"
                        :key="course.id"
                      >
                        <router-link :to="'/course/' + course.id">{{
                          course.OLGDP001
                        }}</router-link>
                        <q-icon name="done" color="green"></q-icon>
                      </li>
                    </ol>
                  </div>
                </div>
              </q-tab-panel>
              <q-tab-panel name="ratings">
                <div>
                  <div class="text-h5">
                    Course Ratings
                    <span v-if="courseRatings.length"
                      >({{ courseRatings.length }})</span
                    >
                  </div>
                  <div class="q-mt-lg q-mb-lg">
                    <q-table
                      row-key="id"
                      :data="courseRatings"
                      :columns="columns"
                      :loading="loadingCourseRatings"
                      :pagination="initialPagination"
                      :rows-per-page-options="[25, 50, 150, 250, 500, 1000, 0]"
                      separator="cell"
                      flat
                      bordered
                      binary-state-sort
                    >
                      <template v-slot:body-cell-comments="props">
                        <q-td :props="props">
                          <div v-if="props.row.comments">
                            <q-icon
                              name="speaker_notes"
                              color="green"
                              :title="props.row.comments"
                            />
                          </div>
                          <div v-else>
                            <q-icon name="speaker_notes_off" color="red" />
                          </div>
                        </q-td>
                      </template>
                      <template v-slot:body-cell-courseName="props">
                        <q-td :props="props">
                          <router-link :to="'/course/' + props.row.courseId">{{
                            props.row.courseName
                          }}</router-link>
                        </q-td>
                      </template>
                    </q-table>
                  </div>
                </div>
              </q-tab-panel>
              <q-tab-panel name="preferences">
                <div class="text-h5">User Preferences</div>
                <q-form @submit.prevent class="q-mt-lg q-mb-lg">
                  <div class="column justify-evenly">
                    <div class="col-12 col-md-4">
                      <div class="q-px-sm q-pb-sm">
                        <q-select
                          clearable
                          filled
                          use-input
                          v-model="preferredCountry"
                          option-value="code"
                          option-label="nameFormatted"
                          :options="countries"
                          label="Preferred Country"
                          emit-value
                          map-options
                          :rules="[val => true]"
                          @filter="preferredCountryFilterFn"
                        />
                      </div>
                    </div>

                    <div class="col-12 col-md-4">
                      <div class="q-px-sm q-pb-sm">
                        <q-select
                          clearable
                          filled
                          use-input
                          option-value="code"
                          option-label="nameFormatted"
                          v-model="preferredCurrency"
                          :options="currencies"
                          label="Preferred Currency"
                          emit-value
                          map-options
                          :rules="[val => true]"
                          @filter="preferredCurrencyFilterFn"
                        />
                      </div>
                    </div>

                    <div class="col-12 col-md-6">
                      <div class="q-px-sm q-pb-sm">
                        <q-select
                          clearable
                          filled
                          option-value="id"
                          option-label="label"
                          v-model="preferredMeasurementSystem"
                          :options="measurementSystemOptions"
                          label="Preferred Measurement System"
                          emit-value
                          map-options
                          :rules="[val => true]"
                        />
                      </div>
                    </div>
                  </div>

                  <q-btn
                    :ripple="false"
                    push
                    color="primary"
                    @click="savePreferences"
                    >Save</q-btn
                  >
                  <q-btn :ripple="false" flat no-caps @click="goBack"
                    >Cancel</q-btn
                  >
                </q-form>
              </q-tab-panel>
            </q-tab-panels>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </q-page>
</template>
<script>
import sortBy from "lodash/sortBy";
import { mapState, mapGetters } from "vuex";
import Banner from "@/components/Banner";
import { getAllCourseRatingsByUid } from "@/apis/course-rating-service";
import US_STATES from "@/data/us-states.json";
import COUNTRIES from "@/data/countries.json";
import CURRENCIES from "@/data/exchange-rates.json";
import { MEASUREMENT_SYSTEMS } from "@/constants";
import { dedupe, sortDate } from "@/utils";

const COUNTRIES_FORMATTED = Object.freeze(
  COUNTRIES.map(c => {
    const nameFormatted = `${c.name} (${c.code})`;

    return {
      ...c,
      nameFormatted,
    };
  }),
);
const CURRENCIES_FORMATTED = Object.freeze(
  CURRENCIES.map(c => {
    const nameFormatted = `${c.name} (${c.code})`;

    return {
      ...c,
      nameFormatted,
    };
  }),
);

export default {
  name: "UserProfile",
  components: {
    Banner,
  },
  data() {
    return {
      tab: "profile",
      firstName: "",
      lastName: "",
      displayName: "",
      gender: null,
      streetAddress: "",
      city: "",
      state: null,
      country: null,
      zipCode: "",
      phoneNumber: "",
      birthDate: "",
      ghin: "",
      handicap: null,
      homeCourse: "",
      isPrivateMember: null,
      roundsPerYear: null,
      tripsPerYear: null,
      designSchool: null,
      coursePreference: null,
      receiveEmails: false,
      coursePreferenceOptions: [
        "Design",
        "Conditions",
        "Amenities/Experience",
        "History",
      ],
      genderOptions: ["Male", "Female"],
      designSchoolOptions: ["Strategic", "Penal", "Heroic"],
      roundsPerYearOptions: ["0-10", "11-25", "26-50", "50-99", "100+"],
      tripsPerYearOptions: ["0", "1-2", "3-5", "6-10", "11+"],
      handicapRangeOptions: ["Plus", "0-9", "10-19", "20+"],
      memberOptions: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
      usStates: US_STATES,
      countries: [],
      currencies: [],
      profileUpdateSuccess: false,
      profileUpdateError: false,
      preferenceUpdateSuccess: false,
      preferenceUpdateError: false,
      preferredCountry: null,
      preferredCurrency: null,
      preferredMeasurementSystem: null,
      measurementSystemOptions: MEASUREMENT_SYSTEMS,
      loadingCourseRatings: false,
      initialPagination: {
        sortBy: "createdAt",
        descending: true,
        rowsPerPage: 25,
      },
      columns: [
        {
          name: "createdAt",
          required: true,
          label: "Created",
          align: "left",
          field: row => row.createdAt,
          format: (val, row) => row.createdAtFormatted,
          sortable: true,
          sort: sortDate,
        },
        {
          name: "courseName",
          required: true,
          label: "Course",
          align: "left",
          field: row => row.courseName,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "datePlayed",
          required: true,
          label: "Date Played",
          align: "center",
          field: row => row.datePlayed,
          format: (val, row) => row.datePlayedFormatted,
          sortable: true,
          sort: sortDate,
        },
        {
          name: "rating_Course",
          required: true,
          label: "Course",
          align: "center",
          field: row => row.rating_Course,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Conditions",
          required: true,
          label: "Conditions",
          align: "center",
          field: row => row.rating_Conditions,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Character",
          required: true,
          label: "Character",
          align: "center",
          field: row => row.rating_Character,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Replay",
          required: true,
          label: "Replay",
          align: "center",
          field: row => row.rating_Replay,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Experience",
          required: true,
          label: "Experience",
          align: "center",
          field: row => row.rating_Experience,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Hang",
          required: true,
          label: "Hang",
          align: "center",
          field: row => row.rating_Hang,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Setting",
          required: true,
          label: "Setting",
          align: "center",
          field: row => row.rating_Setting,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "rating_Amenities",
          required: true,
          label: "Amenities",
          align: "center",
          field: row => row.rating_Amenities,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "comments",
          required: true,
          label: "Comments",
          align: "left",
          field: row => row.comments,
          format: val => (val ? val : "-"),
        },
      ],
      courseRatingsDateFormatOptions: {
        year: "numeric",
        // using 2-digit as opposed to numeric will
        // force a leading zero if applicable
        day: "2-digit",
        month: "2-digit",
      },
      courseRatingsDateFormatter: null,
      courseRatings: [],
      courseRatingsColumns: [{}],
      courseRatingsFilter: "",
    };
  },
  created() {
    const options = this.courseRatingsDateFormatOptions;

    this.courseRatingsDateFormatter = new Intl.DateTimeFormat("en-US", options);
  },
  mounted() {},
  watch: {
    userProfile: {
      handler: function(newVal) {
        this.firstName = newVal.firstName;
        this.lastName = newVal.lastName;
        this.displayName = newVal.displayName;
        this.gender = newVal.gender;
        this.streetAddress = newVal.streetAddress;
        this.city = newVal.city;
        this.state = newVal.state;
        this.zipCode = newVal.zipCode;
        this.country = newVal.country;
        this.phoneNumber = newVal.phoneNumber;
        this.birthDate = newVal.birthDate;
        this.ghin = newVal.ghin;
        this.handicap = newVal.handicap;
        this.homeCourse = newVal.homeCourse;
        this.tripsPerYear = newVal.tripsPerYear;
        this.roundsPerYear = newVal.roundsPerYear;
        this.designSchool = newVal.designSchool;
        this.coursePreference = newVal.coursePreference;
        this.isPrivateMember = newVal.isPrivateMember;
        this.preferredCountry = newVal.preferredCountry;
        this.preferredCurrency = newVal.preferredCurrency;
        this.preferredMeasurementSystem = newVal.preferredMeasurementSystem;
        this.receiveEmails = Boolean(newVal.receiveEmails);
      },
      immediate: true,
    },
    idToken: {
      handler(newToken) {
        if (!newToken) {
          return;
        }

        this.fetchAllCourseRatings();
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters(["courses", "coursePlayedCourses"]),
    ...mapState(["idToken", "userProfile"]),
    isValidDisplayName() {
      return this.displayName && this.displayName.length > 0;
    },
    isValidFirstName() {
      return this.firstName && this.firstName.length > 0;
    },
    isValidLastName() {
      return this.lastName && this.lastName.length > 0;
    },
    isSaveButtonEnabled() {
      return (
        this.isValidDisplayName && this.isValidFirstName && this.isValidLastName
      );
    },
    updatedUserProfile() {
      const profile = {
        firstName: this.firstName,
        lastName: this.lastName,
        displayName: this.displayName,
        gender: this.gender,
        streetAddress: this.streetAddress,
        city: this.city,
        state: this.state,
        zipCode: this.zipCode,
        country: this.country,
        phoneNumber: this.phoneNumber,
        birthDate: this.birthDate,
        ghin: this.ghin,
        handicap: this.handicap,
        homeCourse: this.homeCourse,
        tripsPerYear: this.tripsPerYear,
        roundsPerYear: this.roundsPerYear,
        designSchool: this.designSchool,
        coursePreference: this.coursePreference,
        isPrivateMember: this.isPrivateMember,
        receiveEmails: this.receiveEmails,
      };
      Object.keys(profile).forEach(key => {
        if (profile[key] === undefined) {
          delete profile[key];
        }
      });
      return profile;
    },
    updatedUserPreferences() {
      const preferences = {
        preferredCountry: this.preferredCountry,
        preferredCurrency: this.preferredCurrency,
        preferredMeasurementSystem: this.preferredMeasurementSystem,
      };
      return preferences;
    },
    coursePlayedCoursesSorted() {
      if (!this.coursePlayedCourses.length) {
        return [];
      }

      const courses = this.coursePlayedCourses;
      const sorted = sortBy(courses, ["course"]);

      return sorted;
    },
    courseCountries() {
      const courses = this.courses;
      const allItems = courses.map(course => course.OLGDP101);
      const deDupedItems = dedupe(allItems);

      deDupedItems.sort();

      return deDupedItems;
    },
    countriesComputed() {
      return COUNTRIES_FORMATTED.filter(c =>
        this.courseCountries.includes(c.name),
      );
    },
    courseCurrencies() {
      const courses = this.courses;
      const allItems = courses.map(course => course.OLGDP103);
      const deDupedItems = dedupe(allItems);

      deDupedItems.sort();

      return deDupedItems;
    },
    currenciesComputed() {
      return CURRENCIES_FORMATTED.filter(c =>
        this.courseCurrencies.includes(c.code),
      );
    },
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    save() {
      this.profileUpdateError = false;
      this.profileUpdateSuccess = false;
      this.$store
        .dispatch("updateUserProfile", this.updatedUserProfile)
        .then(() => {
          this.profileUpdateSuccess = true;
          console.debug(
            "Successfully updated user profile:",
            this.updatedUserProfile,
          );
        })
        .catch(error => {
          console.error(error);
          this.profileUpdateError = true;
        });
    },
    savePreferences() {
      this.preferenceUpdateError = false;
      this.preferenceUpdateSuccess = false;
      this.$store
        .dispatch("updateUserProfile", this.updatedUserPreferences)
        .then(() => {
          this.preferenceUpdateSuccess = true;
          console.debug(
            "Successfully updated user preferences:",
            this.updatedUserPreferences,
          );
        })
        .catch(error => {
          console.error(error);
          this.preferenceUpdateError = true;
        });
    },
    getCourseById(id) {
      if (!id || !this.courses.length) {
        return {};
      }

      return this.courses.find(c => c.id === id);
    },
    fetchAllCourseRatings() {
      const token = this.idToken;

      this.loadingCourseRatings = true;

      return getAllCourseRatingsByUid(token)
        .then(courseRatings => {
          const transformed = this.transformCourseRatings(courseRatings);

          this.courseRatings = transformed;
        })
        .finally(() => {
          this.loadingCourseRatings = false;
        });
    },
    transformCourseRatings(input) {
      const transformed = input.map(review => {
        const { id, courseId, createdAt, datePlayed, comments } = review;
        const datePlayedParsed = Date.parse(datePlayed);
        const datePlayedFormatted = this.courseRatingsDateFormatter.format(
          datePlayedParsed,
        );
        const createdAtParsed = Date.parse(createdAt);
        const createdAtFormatted = this.courseRatingsDateFormatter.format(
          createdAtParsed,
        );
        const course = this.getCourseById(courseId);
        const courseName = course.OLGDP001;
        const ratings = review.UserCourseReview_CourseRatings.map(
          courseRating => {
            const { id, courseRatingAttributeId, rating } = courseRating;
            const attr = courseRating.CourseRatingAttribute;
            const name = attr.name;
            const newCourseRating = {
              id,
              courseRatingAttributeId,
              name,
              rating,
            };

            return newCourseRating;
          },
        );
        const newReview = {
          id,
          courseId,
          courseName,
          createdAt,
          createdAtParsed,
          createdAtFormatted,
          datePlayed,
          datePlayedParsed,
          datePlayedFormatted,
          ratings,
          comments,
        };

        ratings.forEach(rating => {
          const key = `rating_${rating.name}`;
          const value = rating.rating;

          newReview[key] = value;
        });

        return newReview;
      });

      return transformed;
    },
    preferredCountryFilterFn(val, update) {
      update(() => {
        if (!val.length) {
          this.countries = this.countriesComputed;
          return;
        }

        const needle = val.toLowerCase();
        this.countries = this.countriesComputed.filter(
          v => v.nameFormatted.toLowerCase().indexOf(needle) > -1,
        );
      });
    },
    preferredCurrencyFilterFn(val, update) {
      update(() => {
        if (!val.length) {
          this.currencies = this.currenciesComputed;
          return;
        }

        const needle = val.toLowerCase();
        this.currencies = this.currenciesComputed.filter(
          v => v.nameFormatted.toLowerCase().indexOf(needle) > -1,
        );
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.comments {
  white-space: pre-line;
  max-height: 200px;
  overflow: auto;
  min-width: 30ch;
  max-width: 60ch;
}
</style>
